'use client';

import { CandidateNavigationProvider, useCandidateNavigation } from '@components/contexts/CandidateNavigationContext';
import { BestieChatStateProvider, useBestieChatState } from '@components/contexts/BestieChatStateContext';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { JobListContent } from '@components/features/job-list-content';
import { CandidatePromptBar } from '@components/features/candidate-prompt-bar';

interface BestieChatViewProps {
  locale: Locale;
  isAuthenticated: boolean;
}

/**
 * @description Standalone Bestie chat experience — single column, no sidebar.
 *
 * Mounts only the providers the candidate chat needs (search navigation, shared chat stream,
 * filter drawer) and renders the chat content (`JobListContent` in the `new-search` view) with
 * the prompt bar pinned at the bottom in chat mode. No `TwoColumnLayout`, no jobs sidebar.
 */
const BestieChatInner = ({ locale, isAuthenticated }: BestieChatViewProps) => {
  const { navigateToCandidateSearch } = useCandidateNavigation();
  const { submitChat } = useBestieChatState();

  return (
    <div className="flex min-h-screen flex-col bg-surface-100 pb-56 lg:pb-52">
      <JobListContent locale={locale} isAuthenticated={isAuthenticated} />
      <CandidatePromptBar
        locale={locale}
        isAuthenticated={isAuthenticated}
        onSearchSubmit={navigateToCandidateSearch}
        onChatSubmit={submitChat}
      />
    </div>
  );
};

export const BestieChatView = ({ locale, isAuthenticated }: BestieChatViewProps) => (
  <CandidateNavigationProvider locale={locale} initialView="new-search">
    <FilterDrawerProvider>
      <BestieChatStateProvider locale={locale}>
        <BestieChatInner locale={locale} isAuthenticated={isAuthenticated} />
      </BestieChatStateProvider>
    </FilterDrawerProvider>
  </CandidateNavigationProvider>
);
